<template>
  <div>
    <EntityShowHeader :entity="entity" />

    <DataTable
      ref="activitiesTable"
      :columns="columns"
      :fetch-callback="fetchEntityActivities"
      :model="ActivityModel"
      has-row-actions
    >
      <template #tableHeaderActions>
        <b-button
          v-can:activities.create
          variant="primary"
          size="sm"
          @click="openCreateActivityModal"
        >
          <Icon icon="plus-circle" size="14" class="mr-2"/>
          {{ __('activities.button-add-activity') }}
        </b-button>
      </template>

      <template #actionButtons="{ item }">
        <ActionPopover :id="item.id" v-can-any="[ 'activities.update', 'activities.destroy' ]">
          <b-button
            v-can:activities.update
            class="dropdown-item"
            size="sm"
            @click="openEditActivityModal(item.id)"
          >
            <Icon icon="edit" class="mr-2" size="14"/>
            {{ __('buttons.edit') }}
          </b-button>

          <b-button
            v-can:activities.destroy
            class="dropdown-item"
            size="sm"
            @click="remove(item.id)"
          >
            <Icon icon="trash" class="mr-2" size="14"/>
            {{ __('buttons.remove') }}
          </b-button>
        </ActionPopover>
      </template>

      <template #type="{ item }">
        <Badge variant="soft-primary">
          <Icon :icon="item.typeData().icon" size="12" class="mr-2"/>
          {{ item.typeData().label }}
        </Badge>
      </template>

      <template #subject="{ item }">
        <a href="#" @click.prevent="openPreviewActivityModal(item.id)">
          {{ item.subject }}
        </a>
      </template>

      <template #status="{ item }">
        <Badge :variant="item.statusData().badge">
          {{ item.statusData().label }}
        </Badge>
      </template>
    </DataTable>
  </div>
</template>
<script>
import EntityShowHeader from '@/Modules/Entity/Components/EntityShowHeader.vue'
import ActivityModel from '@/Modules/Activity/ActivityModel.js'
import EntityActivityService from '@/Modules/Entity/EntityActivity/EntityActivityService.js'
import DataTable from '@/General/Tables/DataTable.vue'
import EditActivityModal from '@/Modules/Activity/Components/EditActivityModal.vue'
import BaseActivityMixin from '@/Modules/Activity/Mixins/BaseActivityMixin.js'
import PreviewActivityModal from '@/Modules/Activity/Components/PreviewActivityModal.vue'
import EntityService from '@/Modules/Entity/EntityService.js'
import LazyLoadComponent from '@/General/LazyLoadComponent.js'

export default {
  components: { DataTable, EntityShowHeader },

  extends: LazyLoadComponent((to, callback) => {
    EntityService.show(to.params.entityId)
      .then((entity) => {
        callback(function () {
          this.entity = entity
        })
      })
      .catch((error) => {
        callback(function () {
          this.handleResourceError(error, to)
        })
      })
  }),

  mixins: [BaseActivityMixin],

  data() {
    return {
      ActivityModel,
      entity: null,
    }
  },

  computed: {
    entityId() {
      return this.$route.params.entityId
    }
  },

  methods: {
    fetchEntityActivities(parameters) {
      return EntityActivityService.list(this.entityId, parameters)
    },

    openCreateActivityModal() {
      this.showModal({
        component: EditActivityModal,
        label: this.__('activities.button-add-activity'),
        icon: 'schedule',
        class: 'modal-md',
        meta: {
          handleCreate: (payload, callback) => this.handleCreate(payload, callback),
        },
        onSuccess: this.reloadTable,
      })
    },

    openEditActivityModal(id) {
      EntityActivityService.show(this.entityId, id)
        .then(activity => {
          this.showModal({
            component: EditActivityModal,
            label: this.__('activities.edit-title'),
            icon: 'schedule',
            class: 'modal-md',
            meta: {
              activity,
              handleUpdate: (payload, callback) => this.handleUpdate(id, payload, callback),
            },
            onSuccess: this.reloadTable,
          })
        })
    },

    openPreviewActivityModal(id) {
      EntityActivityService.show(this.entityId, id)
        .then(activity => {
          this.showModal({
            component: PreviewActivityModal,
            label: this.__('activities.preview'),
            icon: 'schedule',
            class: 'modal-md',
            meta: {
              activity,
              handleUpdate: (payload, callback) => this.handleUpdate(id, payload, callback),
              handleUpdateReminder: (activityId, payload) => this.handleUpdateReminder(activityId, payload),
              handleUpdateStatus: (activityId, payload) => this.handleUpdateStatus(activityId, payload),
            },
            onSuccess: (reopen = false) => {
              if (reopen) {
                this.openCreateActivityModal()
              }
              this.$refs.activitiesTable.refreshTable()
            }
          })
        })
    },

    remove(id) {
      this.$showConfirmationModal(
        this.__('activities.delete-confirmation'),
        () => {
          EntityActivityService
            .delete(this.entityId, id)
            .then(() => {
              this.$successToast(this.__('activities.deleted'))
              this.$refs.activitiesTable.refreshTable()
            })
        }
      )
    },

    handleUpdate(activityId, payload, callback) {
      EntityActivityService
        .update(this.entityId, activityId, payload)
        .then(() => {
          this.$refs.activitiesTable.refreshTable()
          callback()
        })
        .catch(error => this.setErrorsFromResponse(error))
    },

    handleCreate(payload, callback) {
      EntityActivityService
        .create(this.entityId, payload)
        .then(() => {
          this.$refs.activitiesTable.refreshTable()
          callback()
        })
        .catch(error => this.setErrorsFromResponse(error))
    },

    handleUpdateReminder(activityId, payload) {
      return EntityActivityService.updateReminder(this.entityId, activityId, payload)
    },

    handleUpdateStatus(activityId, payload) {
      return EntityActivityService.updateStatus(this.entityId, activityId, payload)
    },

    reloadTable() {
      if (
          this.$refs &&
          typeof this.$refs.activitiesTable?.refreshTable === 'function'
      ) {
        this.$refs.activitiesTable.refreshTable()
      }
    },
  }
}
</script>

